import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Gauge, Users, ListTodo, AlertOctagon, CalendarDays, Inbox,
  MailX, MailCheck, Flame, TrendingUp, ExternalLink, RotateCcw,
  AlertCircle, Cloud,
} from "lucide-react";

import { API } from "@/lib/api";
import { readToken } from "@/lib/tokenStore";

function authHeaders() {
  const t = readToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const RANGE_OPTIONS = [
  { value: "today", label: "Heute" },
  { value: "7d", label: "7 Tage" },
  { value: "30d", label: "30 Tage" },
  { value: "90d", label: "90 Tage" },
  { value: "all", label: "Alle" },
];

const PRIORITY_TONE = {
  low: "text-zinc-400",
  normal: "text-zinc-300",
  high: "text-amber-300",
  urgent: "text-red-300",
};

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function fmtDay(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });
  } catch { return iso; }
}

export default function DashboardPage() {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/admin/crm/dashboard?range=${range}`, {
        headers: authHeaders(), timeout: 25000,
      });
      setData(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Fehler");
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="journey-dashboard-page" className="space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <h1 className="text-lg font-semibold text-zinc-100">PINE Journey Control</h1>
          <p className="text-sm text-zinc-400">
            Operative Übersicht über Leads, Aufgaben, Termine und Funnel-Zustand.
          </p>
        </div>
        <div className="flex flex-wrap gap-1">
          {RANGE_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => setRange(o.value)}
              data-testid={`journey-dashboard-range-${o.value}`}
              className={[
                "rounded border px-3 py-1.5 text-[11px] transition",
                range === o.value
                  ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                  : "border-white/10 text-zinc-400 hover:border-white/20 hover:bg-white/5",
              ].join(" ")}
            >
              {o.label}
            </button>
          ))}
          <button
            type="button"
            onClick={load}
            data-testid="journey-dashboard-refresh"
            className="inline-flex items-center gap-1 rounded border border-white/10 px-3 py-1.5 text-[11px] text-zinc-300 hover:border-white/20 hover:bg-white/5"
          >
            <RotateCcw className="h-3 w-3" />
            Aktualisieren
          </button>
        </div>
      </header>

      {error ? (
        <div data-testid="journey-dashboard-error" className="rounded-md border border-red-500/30 bg-red-500/[0.05] p-4 text-sm text-red-200">
          <div className="mb-1 flex items-center gap-2 font-medium">
            <AlertCircle className="h-4 w-4" /> Fehler beim Laden
          </div>
          <div className="text-xs">{String(error)}</div>
        </div>
      ) : loading || !data ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-20 animate-pulse rounded bg-white/[0.03]" />
          ))}
        </div>
      ) : (
        <>
          <SummaryGrid summary={data.summary} mail={data.mail} />
          <TodaySection today={data.today} />
          <PipelineSection pipeline={data.pipeline} />
          <SourcesSystemsSection sources={data.sources} systems={data.systems} />
          <MailErrorsSection mail={data.mail} />
        </>
      )}
    </div>
  );
}

// ---- Summary Cards --------------------------------------------------------

function SummaryGrid({ summary, mail }) {
  const cards = [
    { key: "new_leads_today", label: "Neue Leads heute", value: summary.new_leads_today, icon: Inbox, tone: "emerald" },
    { key: "new_leads_30d", label: "Neue Leads 30 Tage", value: summary.new_leads_30d, icon: Users, tone: "sky" },
    { key: "open_tasks_total", label: "Offene Aufgaben", value: summary.open_tasks_total, icon: ListTodo, tone: "zinc" },
    { key: "overdue_tasks_total", label: "Überfällige Aufgaben", value: summary.overdue_tasks_total, icon: AlertOctagon, tone: summary.overdue_tasks_total > 0 ? "red" : "zinc" },
    { key: "appointments_today", label: "Termine heute", value: summary.appointments_today, icon: CalendarDays, tone: "emerald" },
    { key: "leads_without_next_step", label: "Ohne nächsten Schritt", value: summary.leads_without_next_step, icon: AlertCircle, tone: summary.leads_without_next_step > 0 ? "amber" : "zinc" },
    { key: "mail_errors_24h", label: "Mailfehler 24h", value: summary.mail_errors_24h, icon: MailX, tone: (mail?.rate_limited_24h || 0) > 0 ? "amber" : summary.mail_errors_24h > 0 ? "red" : "zinc" },
  ];
  const toneMap = {
    emerald: "border-emerald-500/30 text-emerald-300",
    sky: "border-sky-500/30 text-sky-300",
    amber: "border-amber-500/40 text-amber-300",
    red: "border-red-500/40 text-red-300",
    zinc: "border-white/10 text-zinc-200",
  };
  return (
    <section className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <div
            key={c.key}
            data-testid={`journey-dashboard-card-${c.key}`}
            className={`rounded-md border bg-white/[0.01] p-3 ${toneMap[c.tone]}`}
          >
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">{c.label}</span>
              <Icon className="h-3.5 w-3.5" />
            </div>
            <div className="mt-2 text-2xl font-semibold">{c.value}</div>
          </div>
        );
      })}
    </section>
  );
}

// ---- Today block ----------------------------------------------------------

function TodaySection({ today }) {
  const sections = [
    { key: "overdue_tasks", title: "Überfällige Aufgaben", icon: AlertOctagon, tone: "red", renderItem: renderTaskItem, items: today.overdue_tasks, emptyMsg: "Keine überfälligen Aufgaben." },
    { key: "due_tasks_today", title: "Heute fällige Aufgaben", icon: ListTodo, tone: "amber", renderItem: renderTaskItem, items: today.due_tasks_today, emptyMsg: "Keine fälligen Aufgaben." },
    { key: "appointments_today", title: "Termine heute", icon: CalendarDays, tone: "emerald", renderItem: renderAppointmentItem, items: today.appointments_today, emptyMsg: "Keine Termine heute." },
    { key: "new_leads_today", title: "Neue Leads heute", icon: Inbox, tone: "sky", renderItem: renderLeadItem, items: today.new_leads_today, emptyMsg: "Keine neuen Leads heute." },
    { key: "leads_without_next_step", title: "Leads ohne nächsten Schritt", icon: AlertCircle, tone: "zinc", renderItem: renderLeadItem, items: today.leads_without_next_step, emptyMsg: "Alle Leads haben einen nächsten Schritt." },
  ];
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold text-zinc-100">Heute wichtig</h2>
      <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
        {sections.map((s) => {
          const Icon = s.icon;
          return (
            <div
              key={s.key}
              data-testid={`journey-dashboard-today-${s.key}`}
              className="rounded-md border border-white/[0.06] bg-white/[0.01] p-4"
            >
              <header className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs font-medium text-zinc-200">
                  <Icon className="h-3.5 w-3.5" />
                  {s.title}
                </div>
                <span className="rounded border border-white/10 bg-white/[0.04] px-1.5 py-0.5 text-[10px] text-zinc-300">
                  {s.items.length}
                </span>
              </header>
              {s.items.length === 0 ? (
                <p className="text-[11px] text-zinc-500">{s.emptyMsg}</p>
              ) : (
                <ul className="space-y-1.5">
                  {s.items.map((it, i) => <li key={it.id || it.lead_id || i}>{s.renderItem(it)}</li>)}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function renderTaskItem(t) {
  return (
    <Link
      to={t.lead ? `/admin/journey/leads/${t.lead.lead_id}` : "/admin/journey/tasks"}
      data-testid={`journey-dashboard-task-${t.id}`}
      className="flex items-center justify-between gap-2 rounded border border-white/[0.06] bg-white/[0.02] px-2 py-1.5 text-[11px] transition hover:border-emerald-500/30 hover:bg-emerald-500/[0.04]"
    >
      <div className="min-w-0 flex-1">
        <div className={`truncate ${PRIORITY_TONE[t.priority] || "text-zinc-300"}`}>{t.title}</div>
        <div className="truncate text-[10px] text-zinc-500">
          {t.lead?.full_name || t.lead?.company || "—"}
          {t.due_at ? ` · ${fmtDate(t.due_at)}` : ""}
        </div>
      </div>
      <ExternalLink className="h-3 w-3 shrink-0 text-zinc-500" />
    </Link>
  );
}

function renderAppointmentItem(a) {
  return (
    <Link
      to={a.lead ? `/admin/journey/leads/${a.lead.lead_id}` : "/admin/journey/calendar"}
      data-testid={`journey-dashboard-appointment-${a.id}`}
      className="flex items-center justify-between gap-2 rounded border border-white/[0.06] bg-white/[0.02] px-2 py-1.5 text-[11px] transition hover:border-emerald-500/30 hover:bg-emerald-500/[0.04]"
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-zinc-200">{a.title}</div>
        <div className="truncate text-[10px] text-zinc-500">
          {a.lead?.full_name || a.lead?.company || "—"} · {fmtDate(a.starts_at)}
        </div>
      </div>
      <ExternalLink className="h-3 w-3 shrink-0 text-zinc-500" />
    </Link>
  );
}

function renderLeadItem(l) {
  return (
    <Link
      to={`/admin/journey/leads/${l.lead_id}`}
      data-testid={`journey-dashboard-lead-${l.lead_id}`}
      className="flex items-center justify-between gap-2 rounded border border-white/[0.06] bg-white/[0.02] px-2 py-1.5 text-[11px] transition hover:border-emerald-500/30 hover:bg-emerald-500/[0.04]"
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-zinc-200">{l.full_name || l.email || "—"}</div>
        <div className="truncate text-[10px] text-zinc-500">
          {l.company ? `${l.company} · ` : ""}{l.kind_label} · {l.pipeline_stage_label}
        </div>
      </div>
      <ExternalLink className="h-3 w-3 shrink-0 text-zinc-500" />
    </Link>
  );
}

// ---- Pipeline section -----------------------------------------------------

function PipelineSection({ pipeline }) {
  const max = Math.max(1, ...pipeline.stages.map((s) => s.count));
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold text-zinc-100">Pipeline-Verteilung</h2>
      <div className="grid gap-3 lg:grid-cols-2">
        <div data-testid="journey-dashboard-pipeline-bars" className="space-y-1.5 rounded-md border border-white/[0.06] bg-white/[0.01] p-4">
          {pipeline.stages.map((s) => (
            <div key={s.key} className="flex items-center gap-2">
              <div className="w-44 shrink-0 truncate text-[11px] text-zinc-400">{s.label}</div>
              <div className="relative flex-1 overflow-hidden rounded bg-white/[0.03]">
                <div
                  className="h-3 rounded bg-emerald-500/50 transition-all"
                  style={{ width: `${(s.count / max) * 100}%` }}
                />
              </div>
              <div className="w-8 shrink-0 text-right text-[11px] text-zinc-300">{s.count}</div>
            </div>
          ))}
          <div className="mt-3 flex flex-wrap justify-between gap-2 border-t border-white/[0.06] pt-2 text-[10px] text-zinc-500">
            <span>Aktive Leads: <span className="text-zinc-300">{pipeline.active_count}</span></span>
            <span>Abgeschlossen: <span className="text-zinc-300">{pipeline.closed_count}</span></span>
            <span>Opportunities: <span className="text-emerald-300">{pipeline.opportunity_count}</span></span>
          </div>
        </div>

        <div data-testid="journey-dashboard-stage-pressure" className="rounded-md border border-white/[0.06] bg-white/[0.01] p-4">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-zinc-200">
            <Flame className="h-3.5 w-3.5 text-amber-300" />
            Stage-Druck
          </div>
          {pipeline.stage_pressure.length === 0 ? (
            <p className="text-[11px] text-zinc-500">Aktuell kein nennenswerter Druck in der Pipeline.</p>
          ) : (
            <ul className="space-y-1.5">
              {pipeline.stage_pressure
                .filter((sp) => sp.older_than_7d_count > 0 || sp.without_next_step_count > 0)
                .slice(0, 8).map((sp) => (
                <li key={sp.key} className="rounded border border-white/[0.06] bg-white/[0.01] px-2.5 py-1.5 text-[11px]">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-200">{sp.label}</span>
                    <span className="text-zinc-500">{sp.count} Leads</span>
                  </div>
                  <div className="mt-0.5 flex flex-wrap gap-2 text-[10px] text-zinc-500">
                    {sp.older_than_7d_count > 0 ? <span className="text-amber-300">{sp.older_than_7d_count} seit &gt;7 Tagen</span> : null}
                    {sp.without_next_step_count > 0 ? <span className="text-red-300">{sp.without_next_step_count} ohne nächsten Schritt</span> : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

// ---- Sources & Systems ----------------------------------------------------

function SourcesSystemsSection({ sources, systems }) {
  return (
    <section className="grid gap-3 lg:grid-cols-2">
      <BreakdownCard testId="journey-dashboard-sources" title="Leadquellen" icon={TrendingUp} rows={sources.by_source_type} />
      <BreakdownCard
        testId="journey-dashboard-systems"
        title="Systeminteresse"
        icon={Gauge}
        rows={systems.top_system_interests}
        labelKey="key"
      />
    </section>
  );
}

function BreakdownCard({ title, icon, rows, testId, labelKey }) {
  const Icon = icon;
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div data-testid={testId} className="rounded-md border border-white/[0.06] bg-white/[0.01] p-4">
      <header className="mb-2 flex items-center gap-1.5 text-xs font-medium text-zinc-200">
        <Icon className="h-3.5 w-3.5" />
        {title}
      </header>
      {rows.length === 0 ? (
        <p className="text-[11px] text-zinc-500">Keine Daten verfügbar.</p>
      ) : (
        <ul className="space-y-1.5">
          {rows.slice(0, 8).map((r) => (
            <li key={r.key} className="flex items-center gap-2">
              <div className="w-32 shrink-0 truncate text-[11px] text-zinc-400">
                {labelKey ? r[labelKey] : (r.label || r.key)}
              </div>
              <div className="relative flex-1 overflow-hidden rounded bg-white/[0.03]">
                <div className="h-2.5 rounded bg-sky-500/40" style={{ width: `${(r.count / max) * 100}%` }} />
              </div>
              <div className="w-8 shrink-0 text-right text-[11px] text-zinc-300">{r.count}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---- Mail errors ----------------------------------------------------------

function MailErrorsSection({ mail }) {
  const showWarn = (mail.rate_limited_24h || 0) > 0;
  return (
    <section data-testid="journey-dashboard-mail" className="rounded-md border border-white/[0.06] bg-white/[0.01] p-4">
      <header className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-zinc-100">Mailstatus letzte 24 h</h2>
        {showWarn ? (
          <span data-testid="journey-dashboard-mail-quota-warning" className="rounded border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-300">
            Resend-Quota oder Rate Limit erkannt
          </span>
        ) : null}
      </header>
      <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <MailCounter testId="journey-dashboard-mail-sent" label="Gesendet" value={mail.sent_24h} icon={MailCheck} tone="emerald" />
        <MailCounter testId="journey-dashboard-mail-failed" label="Fehlgeschlagen" value={mail.failed_24h} icon={MailX} tone={mail.failed_24h > 0 ? "red" : "zinc"} />
        <MailCounter testId="journey-dashboard-mail-rate-limited" label="Rate Limit" value={mail.rate_limited_24h} icon={Cloud} tone={mail.rate_limited_24h > 0 ? "amber" : "zinc"} />
        <MailCounter testId="journey-dashboard-mail-prepared" label="Vorbereitet" value={mail.prepared_24h} icon={MailCheck} tone="sky" />
      </div>

      {mail.top_errors_24h?.length ? (
        <div className="mb-3">
          <div className="mb-1 text-[10px] uppercase tracking-[0.18em] text-zinc-500">Top-Fehler</div>
          <ul className="space-y-1">
            {mail.top_errors_24h.map((e, i) => (
              <li key={i} className="rounded border border-white/[0.06] bg-white/[0.01] px-2 py-1 text-[11px] text-zinc-300">
                <span className="mr-2 inline-block w-6 text-right text-zinc-500">{e.count}×</span>
                {e.error}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {mail.recent_failures?.length ? (
        <div>
          <div className="mb-1 text-[10px] uppercase tracking-[0.18em] text-zinc-500">Letzte Fehler</div>
          <ul className="space-y-1">
            {mail.recent_failures.slice(0, 5).map((f) => (
              <li key={f.id} className="text-[11px]">
                <Link
                  to={f.lead_id ? `/admin/journey/leads/${f.lead_id}` : "#"}
                  className="flex items-center justify-between gap-2 rounded border border-white/[0.06] bg-white/[0.01] px-2 py-1 hover:border-red-500/30 hover:bg-red-500/[0.04]"
                >
                  <span className="min-w-0 flex-1 truncate text-zinc-300">
                    {f.lead_name || f.kind} · <span className="text-zinc-500">{f.error}</span>
                  </span>
                  <span className="shrink-0 text-[10px] text-zinc-500">{fmtDay(f.created_at)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

function MailCounter({ label, value, icon, tone, testId }) {
  const Icon = icon;
  const tones = {
    emerald: "border-emerald-500/30 text-emerald-300",
    red: "border-red-500/40 text-red-300",
    amber: "border-amber-500/40 text-amber-300",
    sky: "border-sky-500/30 text-sky-300",
    zinc: "border-white/10 text-zinc-200",
  };
  return (
    <div data-testid={testId} className={`rounded-md border bg-white/[0.01] p-2.5 ${tones[tone]}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">{label}</span>
        <Icon className="h-3 w-3" />
      </div>
      <div className="mt-1 text-lg font-semibold">{value || 0}</div>
    </div>
  );
}
